/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { task } from 'ember-concurrency';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { HTMLElementEvent } from 'common/utils/forms';
import type ApiService from 'api/services/api';
import type {
  HashicorpCloudNetwork20200907CreateDNSForwardingRuleRequest as CreateDNSForwardingRuleRequest,
  HashicorpCloudNetwork20200907CreateDNSForwardingRequest as CreateDNSForwardingRequest,
  HashicorpCloudNetwork20200907DNSForwarding as DNSForwarding,
  HashicorpCloudNetwork20200907Peering as Peering,
} from '@clients/cloud-network';
import type RouterService from '@ember/routing/router-service';
import type FlashMessagesService from 'ember-cli-flash/services/flash-messages';
import type { IntlService } from 'ember-intl';

interface Args {
  model: {
    network: {
      id: string;
    };
    project: {
      id: string;
    };
    organization: {
      id: string;
    };
    dnsForwardings: DNSForwarding[];
    peerings: Peering[];
  };
}

export default class PageHvnDnsForwardingCreateComponent extends Component<Args> {
  @service declare readonly api: ApiService;
  @service declare readonly router: RouterService;
  @service declare readonly flashMessages: FlashMessagesService;
  @service declare readonly intl: IntlService;

  @tracked peering = '';
  @tracked IPs: string[] = [];
  @tracked domain = '';

  @action updatePeeringSelect(event: HTMLElementEvent<HTMLSelectElement>) {
    this.peering = event.target.value;
  }

  @action updateIPs(event: HTMLElementEvent<HTMLInputElement>) {
    this.IPs = event.target.value.split(',').map((ip) => ip.trim());
  }

  @action updateDomain(event: HTMLElementEvent<HTMLInputElement>) {
    this.domain = event.target.value;
  }

  save = task(
    { drop: true },
    async (event: HTMLElementEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const { organization, project, network, dnsForwardings } =
        this.args.model;
      const orgId = organization.id;
      const projectId = project.id;
      const networkId = network.id;

      if (!orgId || !projectId) {
        return;
      }

      if (dnsForwardings?.length) {
        const dnsForwardingId = dnsForwardings[0]?.id as string;
        const body = {
          dnsForwardingRule: {
            hvn: networkId,
            dnsForwardingId,
            rule: {
              inboundEndpointIps: this.IPs,
              domainName: this.domain,
            },
          },
        } as CreateDNSForwardingRuleRequest;
        await this.api.network.createDNSForwardingRule(
          orgId,
          projectId,
          networkId,
          dnsForwardingId,
          body
        );
      } else {
        const body = {
          dnsForwarding: {
            peeringId: this.peering,
            hvn: {
              id: networkId,
            },
            rule: {
              inboundEndpointIps: this.IPs,
              domainName: this.domain,
            },
          },
        } as CreateDNSForwardingRequest;
        await this.api.network.createDNSForwarding(
          orgId,
          projectId,
          networkId,
          body
        );
      }
      this.router.transitionTo(
        'cloud.orgs.detail.projects.detail.hvns.detail.dns-forwarding-rules'
      );
      this.flashMessages.success(
        this.intl.t(
          'components.page.dns-forwarding-rules.create.success.title'
        ),
        {
          content: this.intl.t(
            'components.page.dns-forwarding-rules.create.success.body'
          ),
        }
      );
    }
  );
}
